'use client';

import { useState } from 'react';
import Link from 'next/link';
import MapView from './MapView';
import BriefForm from './BriefForm';
import VoiceAgentPicker, { type AgentPersona } from './VoiceAgentPicker';
import VoiceAgent from './VoiceAgent';

type Mode = 'pick' | 'type' | 'voice';

export default function HomeClient() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [searchComplete, setSearchComplete] = useState(false);
  const [mode, setMode] = useState<Mode>('pick');
  const [selectedPersona, setSelectedPersona] = useState<AgentPersona | null>(null);

  const handleNewSearch = () => {
    setSessionId(null);
    setSearchComplete(false);
    setMode('pick');
    setSelectedPersona(null);
  };

  const handleChooseVoice = (persona: AgentPersona) => {
    setSelectedPersona(persona);
    setMode('voice');
  };

  const handleVoiceComplete = (newSessionId: string) => {
    setSessionId(newSessionId);
  };

  return (
    <>
      <MapView sessionId={sessionId} onSearchComplete={() => setSearchComplete(true)} />
      <div className="pointer-events-none absolute inset-x-0 top-4 z-20 px-4 sm:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between rounded-xl border border-black/10 bg-[#faf8f4]/90 px-4 py-2.5 backdrop-blur">
          <Link href="/" className="pointer-events-auto flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-[#1a4a3a]">
              <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1L2 6v8h4v-4h4v4h4V6L8 1z" fill="white" opacity="0.9" />
              </svg>
            </div>
            <span className="font-serif text-base font-semibold text-[#0f0f0d]">
              Postcode<span className="text-[#2d7a5f]">Prices</span>
            </span>
          </Link>
          <div className="pointer-events-auto flex items-center gap-2">
            <Link
              href="/"
              className="rounded-md border border-black/15 bg-white px-3 py-1.5 text-xs font-medium text-[#3a3935]"
            >
              Sold prices
            </Link>
            <Link
              href="/analytics"
              className="rounded-md bg-[#1a4a3a] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#2d7a5f]"
            >
              Analytics
            </Link>
          </div>
        </div>
      </div>
      <div className="absolute inset-y-0 left-0 z-20 flex items-center px-4 pt-24 sm:px-10 sm:pt-20">
        {mode === 'pick' && (
          <VoiceAgentPicker
            onChooseType={() => setMode('type')}
            onChooseVoice={handleChooseVoice}
          />
        )}
        {mode === 'type' && (
          <div className="flex flex-col gap-2">
            <BriefForm onSearchStart={setSessionId} searchComplete={searchComplete} onNewSearch={handleNewSearch} />
            {!sessionId && (
              <button
                onClick={() => setMode('pick')}
                className="px-1 text-xs text-[#7a7870] transition hover:text-[#0f0f0d]"
              >
                &larr; Back to options
              </button>
            )}
          </div>
        )}
        {mode === 'voice' && selectedPersona && !sessionId && (
          <VoiceAgent
            persona={selectedPersona}
            onComplete={handleVoiceComplete}
            onBack={() => setMode('pick')}
          />
        )}
        {mode === 'voice' && sessionId && (
          <div className="flex w-full max-w-sm flex-col gap-2 overflow-hidden rounded-2xl border border-black/10 bg-white p-5 shadow-lg">
            <div className="flex items-center gap-2 text-sm font-medium text-[#2d7a5f]">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              {searchComplete ? 'Search complete' : 'Search started — check the map!'}
            </div>
            {!searchComplete && (
              <div className="flex items-center gap-2 text-xs text-[#7a7870]">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#d4ede5] border-t-[#2d7a5f]" />
                Searching for properties...
              </div>
            )}
            <button
              type="button"
              onClick={handleNewSearch}
              className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-[#1a4a3a] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2d7a5f] active:scale-[0.98]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              New Search
            </button>
          </div>
        )}
      </div>
    </>
  );
}
